import ReactMarkdown, { Components } from "react-markdown";
import classnames from "classnames";
import remarkGfm from "remark-gfm";

import { TargetType, TextLink } from "components/actions/Links";

import css from "./Markdown.module.scss";

type MarkdownProps = {
  text: string;
  className?: string;
  trackingLinkContext?: (entityId: string) => void;
  targetType?: TargetType;
  customComponents?: Components;
  /**
   * Opt-in: let root-relative internal links (e.g. `/documents/{id}`, `/notices/{guid}`) survive
   * URL sanitisation instead of being stripped. Defaults to off — protocol-relative `//host` URLs
   * stay blocked either way. Used by `AnswerMarkdown` to render in-page preview links.
   */
  allowInternalLinks?: boolean;
};

export function Markdown({
  text,
  className,
  trackingLinkContext,
  targetType,
  customComponents,
  allowInternalLinks = false,
}: MarkdownProps): JSX.Element {
  return (
    <div className={classnames(css.markdown, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url: string) => {
          // Allow fragment links (starting with #) - they don't have a protocol
          if (url.startsWith("#")) {
            return url;
          }
          // Allow data: URLs for images
          if (
            url.startsWith("data:image/jpeg;base64,") ||
            url.startsWith("data:image/png;base64,")
          ) {
            return url;
          }
          // Opt-in: allow root-relative internal links (but never protocol-relative `//host`).
          if (allowInternalLinks && url.startsWith("/") && !url.startsWith("//")) {
            return url;
          }
          const allowedProtocols = ["http", "https", "mailto", "tel", "citation"]; // citation is our custom protocol
          const urlProtocol = (url.match(/^([a-z][a-z0-9+.-]*:)?/i) || [])[0] || "";
          if (allowedProtocols.includes(urlProtocol.slice(0, -1))) {
            return url;
          }
          return ""; // Disallow other protocols
        }}
        components={{
          a: ({ children, href }) => {
            if (!href) return <span>{children}</span>;

            let link = href;
            let isHash = false;
            // convert hash links to absolute links, so that they work both in PDFs and in
            // a browser (and the browser is smart enough not to reload the page)
            if (link.startsWith("#")) {
              const url = new URL(window.location.href);
              url.hash = link;
              link = url.toString();
              isHash = true;
            }

            // extract guid from link for optional tracking
            const regexToFindGuid =
              /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/;
            const guid = link.match(regexToFindGuid);

            // because we are converting hash links to absolute urls, we want to ensure that no
            // further params are added to the url if it's a hash, because otherwise this will trigger
            // a page reload
            return (
              <TextLink
                to={link}
                skipUtmParams={isHash}
                onClick={() => trackingLinkContext && guid && trackingLinkContext(guid[0])}
                targetType={targetType}
              >
                {children}
              </TextLink>
            );
          },
          img: ({ src, style, ...props }) => {
            if (!src) {
              return <img src={src} style={style} {...props} />;
            }
            try {
              //set image size via fragment eg https://example.com/image.png#200x200
              const hash = new URL(src).hash;
              const regex = /^#(\d+)x(\d+)$/;
              const match = regex.exec(hash);
              if (match) {
                const [, width, height] = match;
                style = {
                  ...style,
                  width: `${width}px`,
                  height: `${height}px`,
                };
              }
            } catch (e) {
              // Invalid URL, do nothing
            }

            return <img src={src} {...props} style={style} />;
          },
          ...customComponents,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
